import { Controller } from "@hotwired/stimulus"

/**
 * Trix Editor Controller - Proper lifecycle management
 * Fixes the race conditions and state corruption issues in the old implementation
 * Ensures content loads reliably when switching tabs
 */
export default class extends Controller {
  static targets = ["editor", "input"]
  static values = {
    content: String,
    loading: { type: Boolean, default: false }
  }

  connect() {
    console.log('[trix-editor] Controller connected')

    // Wait for Trix to be fully initialized
    if (this.hasEditorTarget) {
      if (this.editorTarget.editor) {
        // Already initialized
        this.onTrixInitialize()
      } else {
        // Wait for initialization
        this.editorTarget.addEventListener('trix-initialize', this.onTrixInitialize.bind(this), { once: true })
      }
    }
  }

  disconnect() {
    console.log('[trix-editor] Controller disconnected')

    // Clear any pending operations
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout)
      this.loadTimeout = null
    }

    // Remove event listeners
    if (this.hasEditorTarget) {
      this.editorTarget.removeEventListener('trix-initialize', this.onTrixInitialize.bind(this))
      this.editorTarget.removeEventListener('trix-change', this.handleChange.bind(this))
    }
  }

  /**
   * Handle Trix initialization
   */
  onTrixInitialize() {
    console.log('[trix-editor] Trix initialized')

    // Load content if provided
    if (this.hasContentValue && this.contentValue) {
      this.loadContent(this.contentValue)
    }

    // Listen for changes
    if (this.hasEditorTarget) {
      this.editorTarget.addEventListener('trix-change', this.handleChange.bind(this))
    }

    // Dispatch ready event
    this.dispatch('ready', { detail: { editor: this.editor } })
  }

  /**
   * Load content into editor
   * @param {string} content - HTML content to load
   */
  loadContent(content) {
    if (!this.editor) {
      console.warn('[trix-editor] Editor not ready, deferring content load')

      // Retry after editor is initialized
      this.loadTimeout = setTimeout(() => {
        if (this.editor) {
          this.loadContent(content)
        }
      }, 100)
      return
    }

    console.log('[trix-editor] Loading content')
    this.loadingValue = true

    try {
      // Clear existing content first
      this.editor.loadHTML('')

      // Small delay to ensure clean slate
      requestAnimationFrame(() => {
        // Load new content
        this.editor.loadHTML(content)

        // Mark as loaded
        this.loadingValue = false

        console.log('[trix-editor] Content loaded successfully')
      })
    } catch (error) {
      console.error('[trix-editor] Error loading content:', error)
      this.loadingValue = false
    }
  }

  /**
   * Get current content from editor
   * @returns {string} HTML content
   */
  getContent() {
    if (!this.editor) {
      console.warn('[trix-editor] Editor not ready')
      return ''
    }

    return this.editor.getDocument().toString()
  }

  /**
   * Clear editor content
   */
  clear() {
    if (!this.editor) {
      console.warn('[trix-editor] Editor not ready')
      return
    }

    console.log('[trix-editor] Clearing content')
    this.loadingValue = true
    this.editor.loadHTML('')
    this.loadingValue = false
  }

  /**
   * Handle content change
   * @param {Event} event - Trix change event
   */
  handleChange(event) {
    // Ignore changes during loading to prevent false positives
    if (this.loadingValue) {
      console.log('[trix-editor] Ignoring change during loading')
      return
    }

    console.log('[trix-editor] Content changed')

    // Dispatch change event for other controllers (e.g., autosave)
    this.dispatch('change', {
      detail: {
        content: this.getContent(),
        editor: this.editor
      }
    })
  }

  /**
   * Handle content value change
   */
  contentValueChanged() {
    if (this.hasContentValue && this.contentValue && this.editor) {
      this.loadContent(this.contentValue)
    }
  }

  /**
   * Get Trix editor instance
   * @returns {Object} Trix editor
   */
  get editor() {
    return this.hasEditorTarget ? this.editorTarget.editor : null
  }

  /**
   * Check if editor is ready
   * @returns {boolean} True if ready
   */
  get isReady() {
    return !!this.editor
  }

  /**
   * Check if currently loading content
   * @returns {boolean} True if loading
   */
  get isLoading() {
    return this.loadingValue
  }
}
